import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  doc,
  setDoc
} from 'firebase/firestore'
import { auth, authedJsonHeaders, db, ENV_SUFFIX } from '../../firebase'

import { useError, useForm } from '../../hooks'
import TextField from '../Form/TextField'
import Button from '../Button'
import Columns from '../Columns'
import ContextMenu from '../ContextMenu'
import Spinner from '../Spinner'
import useGetOrganizationData from '../../hooks/useGetOrganizationData'

import { v4 as uuidv4 } from 'uuid'
import toast from 'react-hot-toast'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { useFirebaseCRUD } from '../../hooks/useFirebaseCRUD'
import DropdownField from '../Form/DropdownField'

export default function InviteUser({ organizationId }) {
  const navigate = useNavigate()
  const setError = useError()
  const [getFieldProps, formData, formError] = useForm()
  const [userRoles, setUserRoles] = useState([])
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [selectedRoles, setSelectedRoles] = useState([]) // New state for multiple selected roles
  const { getCollection } = useFirebaseCRUD()

  const {
    organization,
    loading: orgLoading,
    error: orgError
  } = useGetOrganizationData(String(organizationId))

  const [personalDataTypes, setPersonalDataTypes] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchPersonalDataTypes() {
      try {
        // Scoped to this organisation. An unfiltered list is refused: the
        // users rule authorises per document, and Firestore proves a list from
        // its CONSTRAINTS, not from the documents it would return.
        // organization.id is a NUMBER in Firestore -- see the type trap note
        // at the top of firestore.rules.
        if (!organizationId) return
        const usersSnap = await getDocs(
          query(
            collection(db, 'users'),
            where('organization.id', '==', Number(organizationId))
          )
        )
        const users = usersSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        const typeMap = {}
        users.forEach((user) => {
          if (Array.isArray(user.personalData)) {
            user.personalData.forEach((pd) => {
              const { type } = pd
              if (type?.typeName && !typeMap[type.typeName]) {
                typeMap[type.typeName] = {
                  id: type.typeName,
                  displayName: type.displayName || type.typeName,
                  typeName: type.typeName
                }
              }
            })
          }
        })

        setPersonalDataTypes(Object.values(typeMap))
      } catch (error) {
        setError(error)
      } finally {
        setLoading(false)
      }
    }

    fetchPersonalDataTypes()
  }, [setError])

  useEffect(() => {
    async function fetchRoles() {
      try {
        if (!organizationId) return

        const rolesArr = await getCollection(
          `organizations/${organizationId}/user-roles`
        )

        setUserRoles(rolesArr)
      } catch (err) {
        console.error('Error fetching roles:', err)
        setError(err)
      }
    }

    // Call once when organizationId changes
    fetchRoles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId])

  // Get available roles (roles not yet selected)
  const availableRoles = userRoles.filter(
    (role) => !selectedRoles.find((selectedRole) => selectedRole.id === role.id)
  )

  // Function to handle role selection from dropdown
  const handleRoleSelect = (e) => {
    const roleId = e.target.value
    if (roleId && !selectedRoles.find((role) => role.id === roleId)) {
      const roleToAdd = userRoles.find((role) => role.id === roleId)
      if (roleToAdd) {
        setSelectedRoles((prev) => [
          ...prev,
          {
            ...roleToAdd,
            bindingId: Date.now() // Generate unique ID for binding
          }
        ])
      }
    }
    setSelectedRoleId('') // Reset dropdown
  }

  // Function to remove role from selection
  const removeRoleFromSelection = (roleId) => {
    setSelectedRoles((prev) => prev.filter((role) => role.id !== roleId))
  }

  if (loading || orgLoading || !organization || !personalDataTypes) {
    return <Spinner />
  }

  async function handleSubmit(e) {
    e.preventDefault()

    let { name, email, displayName, ...personalData } = formData

    personalData = Object.fromEntries(
      Object.entries(personalData).filter(([_, value]) => value !== '')
    )

    try {
      // Email-taken check goes through the Cloud Function, not a query.
      // `users where email == ...` proves nothing to the rules engine and is
      // refused; the function runs on the Admin SDK, so it can answer across
      // organisations, which is what uniqueness actually needs.
      // It throws not-found when the address is free.
      try {
        await httpsCallable(getFunctions(), `getUserByEmail${ENV_SUFFIX}`)({
          email
        })
        toast.error('Email already exists.')
        return
      } catch (err) {
        if (err.code !== 'functions/not-found') throw err
      }

      // Invitations are scoped to this organisation. The invitation token IS
      // the document id, so a global list would hand out every pending token;
      // the rule only opens `list` to a caller querying its own organisation.
      const invitationsRef = collection(db, 'invitations')
      const existingInv = await getDocs(
        query(
          invitationsRef,
          where('organization.id', '==', Number(organizationId)),
          where('email', '==', email)
        )
      )

      if (!existingInv.empty) {
        toast.error('Invitation already exists.')
        return
      }

      // Prepare roleBindings array in the required format
      const roleBindings = selectedRoles.map((role) => ({
        id: role.bindingId, // Use the generated bindingId
        role: {
          id: role.id,
          name: role.name
        }
      }))

      const newInvitationRef = doc(invitationsRef)
      const token = newInvitationRef.id // use Firestore doc ID as token
      const inviteLink = `${process.env.REACT_APP_LIVEALARM_APP_URL}/invite/${token}`

      await setDoc(newInvitationRef, {
        email,
        token, // same as doc id
        status: 'pending',
        organization: {
          id: isNaN(organization.id)
            ? organization.id
            : Number(organization.id),
          name: organization.name
        },
        groupBindings: [],
        roleBindings: roleBindings, // Add the roleBindings array
        displayName,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
      })

      // await emailjs.send(
      //   'service_1kfys3e',
      //   'template_ptm7kz9',
      //   {
      //     to_email: email,
      //     name: email,
      //     invite_link: inviteLink,
      //   },
      //   'oa3v2MeVQfI1Mi5nI'
      // );

      // const functions = getFunctions()
      // const sendInvite = httpsCallable(functions, 'sendInvite')
      // await sendInvite({
      //   to_email: email,
      //   invite_link: inviteLink
      // })

      // The endpoint refuses an unauthenticated call, only accepts an
      // invite_link pointing at this environment's own web app, and no longer
      // accepts roleBindings (it always ignored them).
      const inviteHeaders = await authedJsonHeaders()
      if (!inviteHeaders) {
        throw new Error('Not signed in')
      }

      await fetch(
        `https://us-central1-live-alarm-v2.cloudfunctions.net/sendInvitationEmail${ENV_SUFFIX}`,
        {
          method: 'POST',
          headers: inviteHeaders,
          body: JSON.stringify({
            invite_link: inviteLink,
            email: email
          })
        }
      )

      toast.success('Invitation sent successfully!')
      navigate(`/organizations/${organization.id}`)
    } catch (err) {
      if (err.message === 'validation_error') {
        formError(err.props)
      } else {
        setError(err)
      }
    }
  }

  return (
    <div className='box'>
      <form onSubmit={handleSubmit}>
        <Columns>
          <TextField
            label='Organization'
            value={organization.name}
            readOnly={true}
            required={true}
          />
          <DropdownField
            name='roleId'
            label='User role'
            value={selectedRoleId}
            onChange={handleRoleSelect}
            required={false}>
            <option value=''>Select a role</option>
            {availableRoles.length > 0 ? (
              availableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))
            ) : (
              <option disabled>All roles selected</option>
            )}
          </DropdownField>
        </Columns>
        <Columns>
          <TextField
            label='Email'
            name='email'
            type='email'
            placeholder='Email'
            required={true}
            {...getFieldProps()}
          />
          <TextField
            label='Display Name'
            name='displayName'
            placeholder='Display Name'
            required={true}
            {...getFieldProps()}
          />
        </Columns>
        <Columns>
          {personalDataTypes.map((type) => (
            <TextField
              key={type.id}
              name={type.typeName}
              label={type.displayName}
              placeholder={type.displayName}
              {...getFieldProps()}
            />
          ))}
        </Columns>

        {/* Role Selection Section */}
        <div>
          {/* Selected Roles Display */}
          {selectedRoles.length > 0 && (
            <div className='mt-4'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Selected Roles:
              </label>
              <div className='flex flex-wrap gap-2'>
                {selectedRoles.map((role) => (
                  <div
                    key={role.id}
                    className='bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2'>
                    <span>{role.name}</span>
                    <button
                      type='button'
                      onClick={() => removeRoleFromSelection(role.id)}
                      className='text-blue-600 hover:text-blue-800 font-bold cursor-pointer'>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className='flex justify-end'>
          <Button type='submit' disabled={selectedRoles.length === 0}>
            Send Invite
          </Button>
        </div>
      </form>
    </div>
  )
}
